/**
 * Base Control Component
 * 
 * @copyright NIIT Technologies Ltd. 2017-18
 */
// Angular
import {
    Component, Injectable, Inject, ReflectiveInjector, NgZone,
    ElementRef, OnInit, OnDestroy, AfterContentInit, AfterViewInit, OnChanges,
    ViewChildren, ContentChildren, QueryList, HostListener, ChangeDetectorRef,
    Input, SimpleChanges, forwardRef, Renderer
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
// Core
import { BaseComponent } from './base.component';
import { AccessControl, FunctionAccessControl, AccessControlRights } from '../../core/common/access.data';
import { SharedDataCache } from '../../core/common/shared.data';
//

const valueAccessorCallback = () => {
};

/**
 * Base Control Component for All Control Component
 */
export class ControlComponent extends BaseComponent implements ControlValueAccessor, OnChanges {
    //
    protected modelTouched: () => void = valueAccessorCallback;
    protected modelUpdated: (_: any) => void = valueAccessorCallback;
    //
    private _functionId: string = "";
    private _subFunctionId: string = "";
    private _error: string = "";
    private _access: FunctionAccessControl = null;
    private _disabled: boolean = false;
    private _visible: boolean = true;
    private _value: any = null;
    //

    /**
     * Initialize
     */
    constructor(protected controlZone: NgZone, protected controlElement: ElementRef, protected controlRenderer: Renderer) {
        super(controlZone, controlElement, controlRenderer);
    }

    /**
     * Sets Function Id
     * 
     * @param functionId Function Id
     */
    @Input("functionId")
    public set functionId(functionId: string) {
        if (this._functionId !== functionId) {
            this._functionId = functionId;
            // Update Permission to Control
            this.updatePermission();
            this.modelUpdated(this._functionId);
        }
    }

    /**
     * Sets Sub Function Id
     * 
     * @param subFunctionId Sub Function Id
     */
    @Input("subFunctionId")
    public set subFunctionId(subFunctionId: string) {
        if (this._subFunctionId !== subFunctionId) {
            this._subFunctionId = subFunctionId;
            // Update Permission to Control
            this.updatePermission();
            this.modelUpdated(this._subFunctionId);
        }
    }

    /**
     * Update Permission
     */
    private updatePermission() {
        if (!this._functionId) {
            this._disabled = true;
            this._visible = false;
            return;
        }
        let hasUpdated: boolean = false;
        let functionAC: FunctionAccessControl = SharedDataCache.getFunctionAccessData(this._functionId);
        // If Function Access Control Data Available
        if (functionAC) {
            let ac: AccessControl = functionAC.getFunctionAccess();
            // If Function Access Control Data Available
            if (ac && ac.permission) {
                if (this._subFunctionId) {
                    ac = functionAC.getSubFunctionAccess(this._subFunctionId);
                }
                if (ac && ac.permission) {
                    switch (ac.permission) {
                        case AccessControlRights.READ:
                            hasUpdated = true;
                            this._disabled = true;
                            this._visible = true;
                            break;
                        case AccessControlRights.WRITE:
                            hasUpdated = true;
                            this._disabled = false;
                            this._visible = true;
                            break;
                    }
                }
            }
        }
        if (!hasUpdated) {
            this._disabled = true;
            this._visible = false;
        }
    }

    /**
     * Update Error Message
     * 
     * @param error Error Message
     */
    public set error(error: string) {
        if (this._error !== error) {
            this._error = error;
            this.modelUpdated(this._error);
        }
    }

    /**
     * Gets Error
     */
    public get error() {
        return this._error;
    }

    /**
     * Update Access Control
     * 
     * @param access Function Access Control
     */
    public set access(access: FunctionAccessControl) {
        if (this._access !== access) {
            this._access = access;
            this.modelUpdated(this._access);
        }
    }

    /**
     * Sets Visibility
     */
    @Input("visible")
    public set visible(visible: boolean) {
        if (this._visible !== visible) {
            this._visible = visible;
            this.modelUpdated(this._visible);
        }
    }

    /**
     * Gets Disabled State
     */
    public get visible(): boolean {
        return this._visible;
    }

    /**
     * Sets Disabled State
     */
    @Input("disabled")
    public set disabled(disabled: boolean) {
        if (this._disabled !== disabled) {
            this._disabled = disabled;
            this.modelUpdated(this._disabled);
            this.disabledUpdateCallback();
        }
    }

    /**
     * Gets Disabled
     */
    public get disabled(): boolean {
        return this._disabled;
    }

    /**
     * Calls on Disabled State Update
     */
    public disabledUpdateCallback(): void { }

    /**
     * Sets Value
     */
    @Input("value")
    public set value(value: any) {
        if (this._value !== value) {
            this._value = value;
            this.modelUpdated(this._value);
            this.valueUpdateCallback();
        }
    }

    /**
     * Gets Value
     */
    public get value() {
        return this._value;
    }

    /**
     * Calls on Value Update
     */
    public valueUpdateCallback(): void { }

    /**
     * Write Value (Initial)
     */
    public writeValue(value: any) {
        this._value = value;
        this.valueUpdateCallback();
    }

    /**
     * Register On Model Changed
     */
    public registerOnChange(fn: any) {
        this.modelUpdated = fn;
    }

    /**
     *  Register On Model Touched
     */
    public registerOnTouched(fn: any) {
        this.modelTouched = fn;
    }

    /**
     * On Change
     * 
     * @param changes Change
     */
    public ngOnChanges(changes: SimpleChanges) {
    }

    /**
     * Convert to Upper, If the Text Field is to be Converted to Upper
     */
    protected convertToUpper() {
        let style = null;
        let target = this.controlElement.nativeElement;
        //
        if (window.getComputedStyle) {
            style = window.getComputedStyle(target);
        } else if (target.currentStyle) {
            style = target.currentStyle;
        }
        //
        if (style && style.textTransform && style.textTransform.toLowerCase() == "uppercase") {
            target.value = target.value.toUpperCase();
        }
    }

}